import React, { useEffect, useMemo, useState } from 'react';
import { paymentsAPI, clientsAPI, storesAPI } from '../services/api';
import { ModernCard, ModernTable, ModernInput, ModernSelect, ModernLoadingSpinner, ModernButton, ModernSearchBar, ModernModal, ModernStatCard } from '../components/ui/ModernComponents';
import { formatCurrency } from '../utils/currency';
import { useAuth } from '../contexts/AuthContext';
import { CreditCard, Download, Search, User, Calendar, DollarSign, Filter, Eye, Trash2, ChevronLeft, ChevronRight } from 'lucide-react';

const GestionCheques = () => {
  const [cheques, setCheques] = useState([]);
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [stores, setStores] = useState([]);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({
    client_id: '',
    start_date: '',
    end_date: '',
    cheque_number: '',
    min_amount: '',
    max_amount: '',
    cheque_etat: '',
    paiement_status: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCheques, setSelectedCheques] = useState([]);
  const [editingCheque, setEditingCheque] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [calendarYear, setCalendarYear] = useState(new Date().getFullYear());
  const [selectedMonth, setSelectedMonth] = useState(() => new Date().toISOString().slice(0, 7));
  const { user } = useAuth();

  const monthNames = ['Jan', 'Fev', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Aout', 'Sep', 'Oct', 'Nov', 'Dec'];

  const chequesForSelectedMonth = useMemo(() => {
    if (!selectedMonth) return cheques;
    return cheques.filter((c) => (c.payment_date || '').slice(0, 7) === selectedMonth);
  }, [cheques, selectedMonth]);

  const monthCards = useMemo(
    () =>
      monthNames.map((name, index) => {
        const month = String(index + 1).padStart(2, '0');
        const key = `${calendarYear}-${month}`;
        const data = cheques.filter((c) => (c.payment_date || '').slice(0, 7) === key);
        return {
          key,
          label: name,
          count: data.length,
          amount: data.reduce((sum, c) => sum + Number(c.amount || 0), 0)
        };
      }),
    [calendarYear, cheques]
  );

  // Stats (based on selected month)
  const totalCheques = chequesForSelectedMonth.length;
  const totalAmount = chequesForSelectedMonth.reduce((sum, c) => sum + Number(c.amount || 0), 0);
  const todayYmd = new Date().toISOString().slice(0, 10);
  const todayCheques = chequesForSelectedMonth.filter((c) => (c.payment_date || '').slice(0, 10) === todayYmd);
  const todayAmount = todayCheques.reduce((sum, c) => sum + Number(c.amount || 0), 0);

  useEffect(() => {
    fetchCheques();
    fetchClients();
    if (user?.role === 'admin') {
      fetchStores();
    }
  }, [filters]);

  const fetchCheques = async () => {
    setLoading(true);
    try {
      // Get all payments and filter for cheques
      const res = await paymentsAPI.exportPaymentsJson({ mode: 'cheque' });
      let filteredCheques = res.data.filter(p => p.mode === 'cheque');
      
      // Apply additional filters
      if (filters.client_id) {
        filteredCheques = filteredCheques.filter(c => String(c.client_id) === String(filters.client_id));
      }
      if (filters.start_date) {
        filteredCheques = filteredCheques.filter(c => c.payment_date >= filters.start_date);
      }
      if (filters.end_date) {
        filteredCheques = filteredCheques.filter(c => c.payment_date <= filters.end_date);
      }
      if (filters.cheque_number) {
        filteredCheques = filteredCheques.filter(c => 
          (c.cheque_number || '').toLowerCase().includes(filters.cheque_number.toLowerCase())
        );
      }
      if (filters.min_amount) {
        filteredCheques = filteredCheques.filter(c => Number(c.amount) >= Number(filters.min_amount));
      }
      if (filters.max_amount) {
        filteredCheques = filteredCheques.filter(c => Number(c.amount) <= Number(filters.max_amount));
      }
      if (filters.cheque_etat) {
        filteredCheques = filteredCheques.filter(c => c.cheque_etat === filters.cheque_etat);
      }
      if (filters.paiement_status) {
        filteredCheques = filteredCheques.filter(c => c.paiement_status === filters.paiement_status);
      }
      
      setCheques(filteredCheques);
    } catch (error) {
      console.error('Error fetching cheques:', error);
      setCheques([]);
    }
    setLoading(false);
  };

  const fetchClients = async () => {
    try {
      const res = await clientsAPI.getClients();
      setClients(res.data);
    } catch (error) {
      setClients([]);
    }
  };

  const fetchStores = async () => {
    try {
      const res = await storesAPI.getStores();
      setStores(res.data);
    } catch (error) {
      setStores([]);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleSearch = (searchTerm) => {
    setSearch(searchTerm);
  };

  const handleSelectCheque = (id, checked) => {
    setSelectedCheques(prev => 
      checked ? [...prev, id] : prev.filter(c => c !== id)
    );
  };

  const handleSelectAll = (checked) => {
    setSelectedCheques(checked ? chequesForSelectedMonth.map(c => c.id) : []);
  };

  const handleExportExcel = async () => {
    try {
      const exportParams = { mode: 'cheque', ...filters };
      if (selectedMonth) {
        const [year, month] = selectedMonth.split('-').map(Number);
        const monthStart = `${year}-${String(month).padStart(2, '0')}-01`;
        const monthEnd = new Date(year, month, 0).toISOString().slice(0, 10);
        exportParams.start_date = monthStart;
        exportParams.end_date = monthEnd;
      }

      const response = await paymentsAPI.exportPayments({ 
        ...exportParams
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `cheques_${new Date().toISOString().slice(0, 10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Error exporting cheques:', error);
    }
  };

  const handleEditCheque = (cheque) => {
    setEditingCheque({
      ...cheque,
      cheque_etat: cheque.cheque_etat || 'pas_encaisser',
      paiement_status: cheque.paiement_status || 'non_paye'
    });
    setShowEditModal(true);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    setActionLoading(true);
    try {
      await paymentsAPI.updatePayment(editingCheque.id, editingCheque);
      setShowEditModal(false);
      setEditingCheque(null);
      fetchCheques();
    } catch (error) {
      console.error('Error updating cheque:', error);
    }
    setActionLoading(false);
  };

  const handleDeleteCheque = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce chèque ?')) return;
    
    setActionLoading(true);
    try {
      await paymentsAPI.deletePayment(id);
      fetchCheques();
    } catch (error) {
      console.error('Error deleting cheque:', error);
    }
    setActionLoading(false);
  };

  const handleInlineStatusChange = async (chequeId, field, value) => {
    const previousCheques = cheques;
    setCheques((prev) =>
      prev.map((c) => (c.id === chequeId ? { ...c, [field]: value } : c))
    );
    try {
      await paymentsAPI.updatePayment(chequeId, { [field]: value });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      setCheques(previousCheques);
    }
  };

  const columns = [
    {
      key: 'select',
      label: (
        <input 
          type="checkbox" 
          checked={selectedCheques.length === chequesForSelectedMonth.length && chequesForSelectedMonth.length > 0}
          onChange={e => handleSelectAll(e.target.checked)}
        />
      ),
      render: (v, cheque) => (
        <input 
          type="checkbox" 
          checked={selectedCheques.includes(cheque.id)}
          onChange={e => handleSelectCheque(cheque.id, e.target.checked)}
        />
      )
    },
    {
      key: 'id',
      label: 'ID',
      render: (v) => `#${v}`
    },
    {
      key: 'client_name',
      label: 'Client',
      render: (v, cheque) => cheque.client_name || '-'
    },
    {
      key: 'cheque_number',
      label: 'N° Chèque',
      render: (v) => v || '-'
    },
    {
      key: 'amount',
      label: 'Montant',
      render: (v) => formatCurrency(v)
    },
    {
      key: 'payment_date',
      label: 'Date',
      render: (v) => new Date(v).toLocaleDateString('fr-FR')
    },
    {
      key: 'cheque_etat',
      label: 'Etat Chèque',
      render: (v, cheque) => (
        <select
          value={v || 'pas_encaisser'}
          onChange={(e) => handleInlineStatusChange(cheque.id, 'cheque_etat', e.target.value)}
          className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
            (v || 'pas_encaisser') === 'encaisser'
              ? 'border-emerald-200 bg-emerald-50 text-emerald-800'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          <option value="pas_encaisser">Pas encaisser</option>
          <option value="encaisser">Encaisser</option>
        </select>
      )
    },
    {
      key: 'paiement_status',
      label: 'Status Paiement',
      render: (v, cheque) => (
        <select
          value={v || 'non_paye'}
          onChange={(e) => handleInlineStatusChange(cheque.id, 'paiement_status', e.target.value)}
          className={`rounded-lg border px-2 py-1 text-xs font-semibold ${
            (v || 'non_paye') === 'paye'
              ? 'border-sky-200 bg-sky-50 text-sky-800'
              : 'border-rose-200 bg-rose-50 text-rose-800'
          }`}
        >
          <option value="non_paye">Non paye</option>
          <option value="paye">Paye</option>
        </select>
      )
    },
    {
      key: 'reference_type',
      label: 'Type Référence',
      render: (v) => {
        const types = {
          'sales_order': 'Commande',
          'sale': 'Vente',
          'sales_invoice': 'Facture'
        };
        return types[v] || v;
      }
    },
    {
      key: 'reference_id',
      label: 'Référence',
      render: (v, cheque) => (
        <a 
          href={`/${cheque.reference_type === 'sales_order' ? 'ventes/commandes' : cheque.reference_type === 'sale' ? 'ventes' : 'ventes/factures'}/${v}`}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary-600 hover:text-primary-800 underline"
        >
          #{v}
        </a>
      )
    },
    {
      key: 'note',
      label: 'Note',
      render: (v) => v || '-'
    },
    ...(user?.role === 'admin' ? [{
      key: 'store_name',
      label: 'Magasin',
      render: (v, cheque) => {
        const store = stores.find(s => s.id === cheque.store_id);
        return store ? store.name : '-';
      }
    }] : []),
    {
      key: 'actions',
      label: 'Actions',
      render: (v, cheque) => (
        <div className="flex gap-2">
          <ModernButton
            size="sm"
            variant="primary"
            icon={<Eye className="w-4 h-4" />}
            onClick={() => handleEditCheque(cheque)}
          >
            Voir
          </ModernButton>
          <ModernButton
            size="sm"
            variant="danger"
            icon={<Trash2 className="w-4 h-4" />}
            onClick={() => handleDeleteCheque(cheque.id)}
            disabled={actionLoading}
          >
            Supprimer
          </ModernButton>
        </div>
      )
    }
  ];

  const filteredCheques = chequesForSelectedMonth.filter(cheque => {
    if (!search) return true;
    const searchLower = search.toLowerCase();
    return (
      (cheque.client_name || '').toLowerCase().includes(searchLower) ||
      (cheque.cheque_number || '').toLowerCase().includes(searchLower) ||
      (cheque.note || '').toLowerCase().includes(searchLower) ||
      cheque.id.toString().includes(searchLower)
    );
  });

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        <ModernStatCard 
          title="Total Chèques" 
          value={totalCheques} 
          icon={<CreditCard className="w-7 h-7 text-primary-600" />} 
          variant="primary" 
        />
        <ModernStatCard 
          title="Montant Total" 
          value={formatCurrency(totalAmount)} 
          icon={<DollarSign className="w-7 h-7 text-success-600" />} 
          variant="success" 
        />
        <ModernStatCard 
          title="Chèques Aujourd'hui" 
          value={todayCheques.length} 
          icon={<Calendar className="w-7 h-7 text-info-600" />} 
          variant="info" 
        />
        <ModernStatCard 
          title="Montant Aujourd'hui" 
          value={formatCurrency(todayAmount)} 
          icon={<DollarSign className="w-7 h-7 text-warning-600" />} 
          variant="warning" 
        />
      </div>

      <ModernCard className="p-0 shadow-lg">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between p-6 border-b">
          <div className="flex items-center gap-3">
            <CreditCard className="w-7 h-7 text-primary-600" />
            <h1 className="text-2xl font-bold text-neutral-900">Gestion des Chèques</h1>
          </div>
          <div className="flex items-center gap-2 mt-4 md:mt-0">
            <ModernButton 
              variant="ghost" 
              icon={<Filter className="w-5 h-5" />} 
              onClick={() => setShowFilters(f => !f)}
            >
              Filtres
            </ModernButton>
            <ModernButton 
              variant="primary" 
              icon={<Download className="w-5 h-5" />} 
              onClick={handleExportExcel}
            >
              Exporter Excel
            </ModernButton>
          </div>
        </div>

        <div className="p-6 border-b bg-gradient-to-r from-indigo-50 to-sky-50">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <ModernButton variant="ghost" onClick={() => setCalendarYear((y) => y - 1)} icon={<ChevronLeft className="w-4 h-4" />}>
                {calendarYear - 1}
              </ModernButton>
              <h3 className="text-lg font-bold text-neutral-900">{calendarYear}</h3>
              <ModernButton variant="ghost" onClick={() => setCalendarYear((y) => y + 1)} icon={<ChevronRight className="w-4 h-4" />}>
                {calendarYear + 1}
              </ModernButton>
            </div>
            <div className="flex items-center gap-2">
              {selectedMonth && (
                <ModernButton variant="secondary" size="sm" onClick={() => setSelectedMonth('')}>
                  Tous les mois
                </ModernButton>
              )}
              <ModernButton variant="secondary" size="sm" onClick={() => {
                const current = new Date();
                setCalendarYear(current.getFullYear());
                setSelectedMonth(current.toISOString().slice(0, 7));
              }}>
                Mois actuel
              </ModernButton>
            </div>
          </div>
          <p className="text-xs text-neutral-600 mb-3">Cliquez sur un mois pour afficher les cheques du mois selectionne.</p>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
            {monthCards.map((month) => {
              const selected = selectedMonth === month.key;
              return (
                <button
                  type="button"
                  key={month.key}
                  onClick={() => setSelectedMonth((prev) => (prev === month.key ? '' : month.key))}
                  className={`rounded-xl border p-3 text-left transition ${
                    selected ? 'border-indigo-500 bg-indigo-100 ring-2 ring-indigo-200' : 'border-neutral-200 bg-white hover:border-indigo-300'
                  }`}
                >
                  <p className="text-sm font-semibold text-neutral-900">{month.label}</p>
                  <p className="text-xs text-neutral-500">{month.count} cheque{month.count > 1 ? 's' : ''}</p>
                  <p className="text-xs font-semibold text-indigo-700">{formatCurrency(month.amount)}</p>
                </button>
              );
            })}
          </div>
        </div>

        {showFilters && (
          <div className="p-6 bg-neutral-50 border-b animate-fade-in-up">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <ModernSelect
                label="Client"
                name="client_id"
                value={filters.client_id}
                onChange={handleFilterChange}
                options={[{value: '', label: 'Tous les clients'}, ...clients.map(c => ({value: c.id, label: c.name}))]}
                icon={<User className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="N° Chèque"
                name="cheque_number"
                value={filters.cheque_number}
                onChange={handleFilterChange}
                icon={<CreditCard className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="Date début"
                name="start_date"
                type="date"
                value={filters.start_date}
                onChange={handleFilterChange}
                icon={<Calendar className="w-4 h-4 text-primary-400" />}
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mt-4">
              <ModernInput
                label="Date fin"
                name="end_date"
                type="date"
                value={filters.end_date}
                onChange={handleFilterChange}
                icon={<Calendar className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="Montant min"
                name="min_amount"
                type="number"
                value={filters.min_amount}
                onChange={handleFilterChange}
                icon={<DollarSign className="w-4 h-4 text-primary-400" />}
              />
              <ModernInput
                label="Montant max"
                name="max_amount"
                type="number"
                value={filters.max_amount}
                onChange={handleFilterChange}
                icon={<DollarSign className="w-4 h-4 text-primary-400" />}
              />
              <ModernSelect
                label="Etat cheque"
                name="cheque_etat"
                value={filters.cheque_etat}
                onChange={handleFilterChange}
                options={[
                  { value: '', label: 'Tous' },
                  { value: 'encaisser', label: 'Encaisser' },
                  { value: 'pas_encaisser', label: 'Pas encaisser' }
                ]}
              />
              <ModernSelect
                label="Status paiement"
                name="paiement_status"
                value={filters.paiement_status}
                onChange={handleFilterChange}
                options={[
                  { value: '', label: 'Tous' },
                  { value: 'paye', label: 'Paye' },
                  { value: 'non_paye', label: 'Non paye' }
                ]}
              />
              <ModernSearchBar
                placeholder="Rechercher par client, n° chèque..."
                onSearch={handleSearch}
                debounce={300}
                icon={<Search className="w-4 h-4" />}
              />
            </div>
          </div>
        )}

        <div className="p-6 overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-8">
              <ModernLoadingSpinner size="lg" />
            </div>
          ) : (
            <ModernTable
              columns={columns}
              data={filteredCheques}
              className="w-full"
              rowClassName="hover:bg-neutral-50 even:bg-neutral-50"
            />
          )}
        </div>
      </ModernCard>

      {/* Edit Modal */}
      <ModernModal 
        isOpen={showEditModal} 
        onClose={() => { setShowEditModal(false); setEditingCheque(null); }} 
        title="Modifier le Chèque" 
        size="md"
      >
        {editingCheque && (
          <form onSubmit={handleSaveEdit} className="space-y-4">
            <ModernInput
              label="N° Chèque"
              name="cheque_number"
              value={editingCheque.cheque_number || ''}
              onChange={e => setEditingCheque(prev => ({ ...prev, cheque_number: e.target.value }))}
              required
            />
            <ModernInput
              label="Montant"
              name="amount"
              type="number"
              min="0.01"
              step="0.01"
              value={editingCheque.amount}
              onChange={e => setEditingCheque(prev => ({ ...prev, amount: e.target.value }))}
              required
            />
            <ModernInput
              label="Date"
              name="payment_date"
              type="date"
              value={editingCheque.payment_date ? editingCheque.payment_date.slice(0, 10) : ''}
              onChange={e => setEditingCheque(prev => ({ ...prev, payment_date: e.target.value }))}
              required
            />
            <ModernInput
              label="Note"
              name="note"
              value={editingCheque.note || ''}
              onChange={e => setEditingCheque(prev => ({ ...prev, note: e.target.value }))}
            />
            <ModernSelect
              label="Etat cheque"
              name="cheque_etat"
              value={editingCheque.cheque_etat || 'pas_encaisser'}
              onChange={e => setEditingCheque(prev => ({ ...prev, cheque_etat: e.target.value }))}
              options={[
                { value: 'pas_encaisser', label: 'Pas encaisser' },
                { value: 'encaisser', label: 'Encaisser' }
              ]}
            />
            <ModernSelect
              label="Status paiement"
              name="paiement_status"
              value={editingCheque.paiement_status || 'non_paye'}
              onChange={e => setEditingCheque(prev => ({ ...prev, paiement_status: e.target.value }))}
              options={[
                { value: 'non_paye', label: 'Non paye' },
                { value: 'paye', label: 'Paye' }
              ]}
            />
            <div className="flex justify-end gap-2">
              <ModernButton 
                type="button" 
                variant="secondary" 
                onClick={() => { setShowEditModal(false); setEditingCheque(null); }}
              >
                Annuler
              </ModernButton>
              <ModernButton 
                type="submit" 
                variant="primary" 
                loading={actionLoading}
              >
                Enregistrer
              </ModernButton>
            </div>
          </form>
        )}
      </ModernModal>
    </div>
  );
};

export default GestionCheques; 